import { FC, useCallback, useMemo, useState } from 'react';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { CloudFoundryApplication } from './types';
import { CloudFoundryFilter } from './CloudFoundryFilter';

/**
 * Column widths for the Cloud Foundry table
 */
const COLUMN_WIDTHS = {
  organization: '30%',
  datacenter: '20%',
  cfAppName: '30%',
  status: '10%',
  site: '10%',
} as const;

/**
 * Pagination options for rows per page
 */
const ROWS_PER_PAGE_OPTIONS = [10, 25, 50, 100] as const;

/**
 * Default number of rows per page
 */
const DEFAULT_ROWS_PER_PAGE = 10;

/**
 * Fields to include in global search
 */
const GLOBAL_FILTER_FIELDS = ['orgName', 'datacenter', 'cfAppName'] as const;

/**
 * Props for CloudFoundryTable component
 */
interface CloudFoundryTableProps {
  /** Array of Cloud Foundry applications to display */
  applications: CloudFoundryApplication[];
  /** Whether the table is in a loading state */
  loading?: boolean;
  /** Total count of applications (for pagination) */
  totalCount?: number;
}

/**
 * Table component for displaying Cloud Foundry applications
 * Features: global search, sorting, pagination
 *
 * @param props - Component props
 * @returns Table component with Cloud Foundry applications
 */
export const CloudFoundryTable: FC<CloudFoundryTableProps> = ({
  applications,
  loading = false,
  totalCount,
}) => {
  const [globalFilterValue, setGlobalFilterValue] = useState<string>('');

  /**
   * Handler for clearing the global filter
   */
  const handleClearFilter = useCallback(() => {
    setGlobalFilterValue('');
  }, []);

  /**
   * Handler for filter input change
   */
  const handleFilterChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setGlobalFilterValue(e.target.value);
    },
    [],
  );

  /**
   * Header component with filter input
   */
  const tableHeader = useMemo(
    () => (
      <CloudFoundryFilter
        onChange={handleFilterChange}
        onClear={handleClearFilter}
        placeholder="Filter by Organization, Datacenter, or CF App Name"
        style={{ fontSize: '90%' }}
        value={globalFilterValue}
      />
    ),
    [globalFilterValue, handleFilterChange, handleClearFilter],
  );

  return (
    <DataTable
      value={applications}
      globalFilter={globalFilterValue}
      globalFilterFields={[...GLOBAL_FILTER_FIELDS]}
      header={tableHeader}
      loading={loading}
      paginator
      rows={DEFAULT_ROWS_PER_PAGE}
      rowsPerPageOptions={[...ROWS_PER_PAGE_OPTIONS]}
      sortMode="multiple"
      emptyMessage="No Cloud Foundry applications found"
      responsiveLayout="scroll"
      tableStyle={{ minWidth: '50rem', fontSize: '0.875rem' }}
      size="small"
      style={{ lineHeight: '1' }}
      totalRecords={totalCount}
    >
      <Column
        field="orgName"
        header="Organization"
        sortable
        style={{ width: COLUMN_WIDTHS.organization }}
      />
      <Column
        field="datacenter"
        header="Datacenter"
        sortable
        style={{ width: COLUMN_WIDTHS.datacenter }}
      />
      <Column
        field="cfAppName"
        header="CF App Name"
        sortable
        style={{ width: COLUMN_WIDTHS.cfAppName }}
      />
      <Column
        field="status"
        header="Status"
        sortable
        style={{ width: COLUMN_WIDTHS.status }}
      />
      <Column
        field="site"
        header="Site"
        sortable
        style={{ width: COLUMN_WIDTHS.site }}
      />
    </DataTable>
  );
};
